import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, CalendarClock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useBilling } from "@/context/BillingContext";
import { money } from "@/pages/Pricing";
import { api, formatApiError } from "@/lib/api";
import { useCreatorOn } from "@/lib/platform";

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const StatusPill = ({ sub }) => {
  const label = !sub ? "Free" : !sub.live ? sub.status : sub.cancel_at_period_end ? "Cancels at period end" : sub.source === "manual" ? "Active · granted" : "Active";
  return <span data-testid="billing-status" className={`rounded-full border px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.18em] ${sub?.live && !sub.cancel_at_period_end ? "border-[#FFD86A]/70 text-[#FFD86A]" : "border-white/20 text-white/60"}`}>{label}</span>;
};

export const BillingPanel = () => {
  const creatorOn = useCreatorOn();
  const { billing, refresh } = useBilling();
  const [busy, setBusy] = useState(false);
  if (!billing) return <p className="text-sm text-[#8B8B8B]">Loading billing…</p>;
  const { entitlements: ent, subscription: sub, payments } = billing;
  const stripeLive = sub?.live && sub.source === "stripe";
  const other = ent.plan_code === "studio" ? "creator" : "studio";

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await refresh(); toast.success(msg); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const cancel = () => window.confirm("Cancel your subscription? You keep access until the end of the current period.") && act(() => api.post("/billing/cancel"), "Subscription will end at period end");
  const resume = () => act(() => api.post("/billing/resume"), "Subscription resumed");
  const change = () => window.confirm(`Switch to ${other === "studio" ? "Premium+" : "Premium"}? Stripe prorates the difference immediately (plus applicable taxes).`) && act(() => api.post("/billing/change-plan", { plan_code: other }), "Plan updated");

  return (
    <div data-testid="billing-panel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <p data-testid="billing-plan-name" className="font-display text-3xl font-bold gold-text">{ent.is_admin ? "Admin · full access" : ent.plan_name}</p>
        <StatusPill sub={sub} />
      </div>
      <dl className="grid gap-4 sm:grid-cols-3 text-sm">
        <div><dt className="text-[#8B8B8B] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {sub?.cancel_at_period_end ? "Access until" : "Renews"}</dt><dd data-testid="billing-period-end" className="text-white text-base">{sub?.live ? (sub.current_period_end ? fmtDate(sub.current_period_end) : "No expiry") : "—"}</dd></div>
        {creatorOn && <div><dt className="text-[#8B8B8B] flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#00F0FF]" /> AI credits</dt><dd data-testid="billing-credits" className="text-[#7CF7FF] text-base">{ent.ai_credits_remaining} / {ent.ai_credits_total}</dd></div>}
        <div><dt className="text-[#8B8B8B]">Quality · profiles</dt><dd className="text-white text-base">{ent.quality_label} · {ent.max_profiles}</dd></div>
      </dl>
      <div className="flex flex-wrap gap-3">
        {!stripeLive && !ent.is_admin && <Link to="/pricing" data-testid="billing-upgrade-btn" className="btn-gold px-6 py-2.5 text-sm">{ent.plan_code === "free" ? "Upgrade" : "Subscribe with Stripe"}</Link>}
        {stripeLive && <button data-testid="billing-change-plan-btn" disabled={busy} onClick={change} className="btn-gold px-6 py-2.5 text-sm"><ArrowUpDown className="h-4 w-4" /> {other === "studio" ? "Upgrade to Premium+" : "Downgrade to Premium"}</button>}
        {stripeLive && !sub.cancel_at_period_end && <button data-testid="billing-cancel-btn" disabled={busy} onClick={cancel} className="rounded-full border border-[#E50914]/40 px-6 py-2.5 text-sm font-semibold text-[#ff6b6b] hover:bg-[#E50914]/10 transition-colors">Cancel subscription</button>}
        {stripeLive && sub.cancel_at_period_end && <button data-testid="billing-resume-btn" disabled={busy} onClick={resume} className="btn-ghost-gold px-6 py-2.5 text-sm">Resume subscription</button>}
      </div>
      <div>
        <h3 className="eyebrow !text-[0.68rem] mb-3">Payment history</h3>
        {payments.length ? (
          <div className="overflow-x-auto rounded-xl border border-[#D4AF37]/15">
            <table className="w-full text-sm" data-testid="billing-payments-table">
              <tbody>{payments.map((p) => (
                <tr key={p.id} className="border-b border-[#D4AF37]/10 last:border-0" data-testid="billing-payment-row">
                  <td className="p-3 text-white/70">{fmtDate(p.created_at)}</td>
                  <td className="p-3 text-white/85">{p.description || p.plan_code}</td>
                  <td className="p-3 capitalize text-[#8B8B8B]">{p.kind.replace("_", " ")}</td>
                  <td className="p-3 text-right text-[#FFD86A]">{money(p.amount, p.currency)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p data-testid="billing-no-payments" className="text-sm text-[#8B8B8B]">No payments yet.</p>}
      </div>
      {billing.test_mode && <p className="text-[0.7rem] tracking-[0.15em] text-[#7CF7FF]/70">STRIPE TEST MODE</p>}
      {creatorOn && <CreditLedger />}
    </div>
  );
};

const CreditLedger = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/create/credits/ledger").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows?.length) return null;
  return (
    <div data-testid="credit-ledger">
      <h3 className="eyebrow !text-[0.68rem] mb-3">AI credit ledger</h3>
      <div className="max-h-72 overflow-y-auto rounded-xl border border-[#00F0FF]/15">
        <table className="w-full text-sm"><tbody>{rows.map((r) => (
          <tr key={r.id} data-testid="credit-ledger-row" className="border-b border-[#00F0FF]/10 last:border-0">
            <td className="p-3 text-white/60">{new Date(r.created_at).toLocaleString()}</td>
            <td className="p-3 text-white/85">{r.label}</td>
            <td className="p-3 text-xs uppercase text-[#8B8B8B]">{r.status}</td>
            <td className={`p-3 text-right font-bold ${r.cost < 0 ? "text-[#7CF7FF]" : "text-[#FFD86A]"}`}>{r.unlimited ? "∞" : r.cost < 0 ? `+${-r.cost}` : `−${r.cost}`}</td>
            <td className="p-3 text-right text-xs text-[#8B8B8B]">{r.balance_after ?? ""}</td>
          </tr>
        ))}</tbody></table>
      </div>
    </div>
  );
};
